import {
  defaultSections,
  DEFAULT_BY_ID,
  slugifyId,
} from "./frameworkDefaults";
import { isSectionRequiredForIntake } from "../../utils/chatUtils";
import {
  reconcileGate0FieldAfterMerge,
  reconcileItemGate0Fields,
  shouldKeepGate0Mapping,
} from "./sectionEditorUtils";

/** Bump when default mandatory rules change; v1 saves get all questions set mandatory ON once. */
export const FRAMEWORK_MANDATORY_VERSION = 3;
function itemRequiredFlag(it, baseItem, mandatoryVersion) {
  if ((mandatoryVersion || 0) < FRAMEWORK_MANDATORY_VERSION) {
    return baseItem ? baseItem.required !== false : it.required !== false;
  }
  if (Object.prototype.hasOwnProperty.call(it, "required")) {
    return it.required !== false;
  }
  return baseItem?.required !== false;
}

export function migrateMandatoryDefaults(framework) {
  if (!framework) return null;
  const ver = Number(framework.mandatory_defaults_version) || 0;
  if (ver >= FRAMEWORK_MANDATORY_VERSION) return framework;
  return {
    ...framework,
    mandatory_defaults_version: FRAMEWORK_MANDATORY_VERSION,
    sections: (framework.sections || []).map((s) => ({
      ...s,
      required: true,
      items: (s.items || []).map((it) => ({ ...it, required: true })),
    })),
  };
}

/** Count questions across all sections (uses items[], not items_count). */
export function totalItems(framework) {
  return (framework?.sections || []).reduce(
    (sum, s) => sum + (s.items?.length || s.items_count || 0),
    0
  );
}

export function requiredSectionsCount(framework) {
  return (framework?.sections || []).filter((s) => isSectionRequiredForIntake(s)).length;
}

export { slugifyId } from "./frameworkDefaults";
export function uniqueSectionId(name, existingIds) {
  let base = slugifyId(name);
  let id = base;
  let n = 2;
  while (existingIds.has(id)) {
    id = `${base}_${n}`;
    n += 1;
  }
  return id;
}

export function uniqueItemId(sectionId, existingIds) {
  const prefix = sectionId.slice(0, 3).toUpperCase() || "Q";
  let n = 1;
  let id = `${prefix}${n}`;
  while (existingIds.has(id)) {
    n += 1;
    id = `${prefix}${n}`;
  }
  return id;
}

/** Item ids are global keys in intake captured{} — must be unique across all sections. */
export function assignUniqueItemIds(sectionId, items, globalExistingIds) {
  const used = new Set(globalExistingIds);
  return (items || []).map((it, i) => {
    let id = it.id;
    if (!id || used.has(id)) {
      id = uniqueItemId(sectionId, used);
    }
    used.add(id);
    return { ...it, id };
  });
}

function placeholderItems(sectionId, count) {
  const items = [];
  for (let i = 1; i <= count; i += 1) {
    items.push({
      id: uniqueItemId(sectionId, new Set(items.map((x) => x.id))),
      text: `Question ${i}`,
      required: true,
      extraction_key: `${sectionId}_q${i}`,
    });
  }
  return items;
}

/** Ensure section has items[] synced from defaults or placeholders. */
export function hydrateSection(section, mandatoryVersion = FRAMEWORK_MANDATORY_VERSION) {
  const base = DEFAULT_BY_ID[section.id];
  if (section.items?.length) {
    const baseItemsById = new Map((base?.items || []).map((it) => [it.id, it]));
    const items = section.items.map((it) => {
      const baseItem = baseItemsById.get(it.id);
      const merged = { ...baseItem, ...it };
      merged.required = itemRequiredFlag(it, baseItem, mandatoryVersion);
      return reconcileItemGate0Fields(reconcileGate0FieldAfterMerge(merged, baseItem));
    });
    return {
      ...section,
      items,
      items_count: items.length,
      required: isSectionRequiredForIntake({ ...section, items }),
    };
  }
  if (base?.items?.length) {
    const items = base.items.map((it) => ({ ...it }));
    return {
      ...section,
      items,
      items_count: items.length,
      required: isSectionRequiredForIntake({ ...section, items }),
    };
  }
  const count = Math.max(1, section.items_count || 1);
  const items = placeholderItems(section.id, count);
  return {
    ...section,
    items,
    items_count: items.length,
    required: isSectionRequiredForIntake({ ...section, items }),
  };
}

/** Normalize loaded or preset framework to full items[]. */
export function normalizeFramework(framework) {
  if (!framework) return null;
  const migrated = migrateMandatoryDefaults(framework);
  const mandatoryVersion = Number(migrated.mandatory_defaults_version) || FRAMEWORK_MANDATORY_VERSION;
  const sections = (migrated.sections || []).map((s) => hydrateSection(s, mandatoryVersion));
  return { ...migrated, mandatory_defaults_version: mandatoryVersion, sections };
}

/** Build framework from preset section list (metadata only). */
export function frameworkFromPreset(preset) {
  return normalizeFramework(JSON.parse(JSON.stringify(preset)));
}

export function createSection(existingSections) {
  const ids = new Set(existingSections.map((s) => s.id));
  const name = "New Section";
  const id = uniqueSectionId(name, ids);
  return {
    id,
    name,
    threshold: 85,
    weight: 1.0,
    required: false,
    items: [],
    items_count: 0,
  };
}

export function createItem(section) {
  const ids = new Set((section.items || []).map((it) => it.id));
  const id = uniqueItemId(section.id, ids);
  return {
    id,
    text: "New question",
    required: true,
    extraction_key: `${slugifyId(section.id)}_${slugifyId(id)}`,
  };
}

export function validateFramework(framework) {
  const sections = framework?.sections || [];
  if (!sections.length) return "Framework must have at least one section.";
  for (const s of sections) {
    if (!(s.name || "").trim()) return "Every section needs a name.";
    if (!(s.items || []).length) return `Section "${s.name}" must have at least one question.`;
    for (const it of s.items) {
      if (!(it.text || "").trim()) return `Section "${s.name}" has an empty question.`;
    }
  }
  return null;
}

/** Strip UI-only fields before save. */
export function frameworkForSave(framework) {
  const sections = (framework.sections || []).map(({ items_count, ...s }) => {
    const items = (s.items || []).map((it) => {
      const reconciled = reconcileItemGate0Fields(it);
      const text = (reconciled.text || "").trim();
      const item = { id: reconciled.id, text, required: reconciled.required !== false };
      if (shouldKeepGate0Mapping(reconciled, s.id)) {
        item.gate0_field = reconciled.gate0_field;
        item.extraction_key = reconciled.extraction_key;
      } else {
        item.extraction_key = reconciled.extraction_key || slugifyId(text);
      }
      return item;
    });
    const section = { ...s, items };
    return {
      ...section,
      required: isSectionRequiredForIntake(section),
      items,
    };
  });
  return {
    ...framework,
    mandatory_defaults_version: FRAMEWORK_MANDATORY_VERSION,
    sections,
  };
}

export { defaultSections, DEFAULT_BY_ID };
